import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Button, Modal, Spin, message } from "antd";
import {
  ArrowDownOutlined,
  CloseOutlined,
  FileOutlined,
  NumberOutlined,
  PaperClipOutlined,
  SendOutlined,
  TeamOutlined,
  UserOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import isBetween from "dayjs/plugin/isBetween";
import customParseFormat from "dayjs/plugin/customParseFormat";

// import utils
import getTime from "../../untils/getTime";
import getDateAndTime from "../../untils/getDateAndTime";
import { socket } from "../../untils/socket";
import apiClient from "../../actions/api";
import isEmpty from "../../validations/is-empty";

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isBetween);

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const MAX_FILE_SIZE = 3 * 1024 * 1024;
const MAX_FILES = 10;
const URL_PATTERN = /(https?:\/\/[^\s<]+)/gi;

const Chatbox = ({
  selectedChat,
  joinedUsers,
  groupList,
  onlineUsers,
  markConversationRead,
}) => {
  const dateAndTimeFormat = "YYYY-MM-DD HH:mm";

  const authUser = useSelector((state) => state.auth.user);
  const themeMode = useSelector((state) => state.themeMode);
  const timezoneMode = useSelector((state) => state.timezoneMode);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [previewImage, setPreviewImage] = useState("");
  const [imageZoom, setImageZoom] = useState(1);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [scrollBottomFlag, setScrollBottomFlag] = useState(false);
  const [actionMenu, setActionMenu] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);

  // Pagination states
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const imageDragRef = useRef(null);
  const forceBottomUntilRef = useRef(0);
  const isFetchingRef = useRef(false);
  const prevScrollHeightRef = useRef(0);

  // for typing user
  const [isSomeoneTyping, setIsSeomoneTyping] = useState(false);
  const [typingUser, setTypingUser] = useState(null);
  const typingTimeoutRef = useRef(null);

  useEffect(() => {
    if ("Notification" in window) {
      Notification.requestPermission().then((permission) => {
        console.log("Notification permission:", permission);
      });
    }
  }, []);

  useEffect(() => {
    if (!isEmpty(authUser)) {
      socket.on("receive_message", (data) => {
        if (data.senderId !== authUser._id) {
          const group = groupList.find((u) => u._id === data.roomId);
          const user = joinedUsers.find((u) => u._id === data.senderId);
          showNotification(
            `${group?.name || "Team chat"} - ${user?.name || "Someone"}`,
            data.message || data.attachments?.[0]?.name || "Sent an attachment",
            user?.avatar,
          );
        }

        if (!isEmpty(selectedChat)) {
          if (selectedChat.id === data.roomId) {
            setMessages((prev) => [...prev, data]);
            forceBottomUntilRef.current = Date.now() + 5000;
            setTimeout(() => scrollToDirectBottom(), 0);
          }
        }
      });

      socket.on("receive_dm", (data) => {
        if (data.senderId !== authUser._id) {
          const user = joinedUsers.find((u) => u._id === data.senderId);
          showNotification(
            user?.name || "Someone",
            data.message || data.attachments?.[0]?.name || "Sent an attachment",
            user?.avatar,
          );
        }

        if (!isEmpty(selectedChat)) {
          if (
            selectedChat.type === "dm" &&
            (selectedChat.id === data.senderId ||
              selectedChat.id === data.receiverId)
          ) {
            setMessages((prev) => [...prev, data]);
            forceBottomUntilRef.current = Date.now() + 5000;
            setTimeout(() => scrollToDirectBottom(), 0);
          }
        }
      });

      socket.on("receive_own", (data) => {
        if (!isEmpty(selectedChat)) {
          if (
            selectedChat.type === "dm" &&
            selectedChat.id === data.receiverId
          ) {
            setMessages((prev) => [...prev, data]);
            forceBottomUntilRef.current = Date.now() + 5000;
            setTimeout(() => scrollToDirectBottom(), 0);
          }
        }
      });

      socket.on("typing", (data) => {
        if (!isEmpty(selectedChat)) {
          if (data.type === selectedChat.type) {
            if (selectedChat.type === "dm") {
              if (
                data.senderId !== authUser._id &&
                selectedChat.id === data.senderId
              ) {
                const user = joinedUsers.find((u) => u._id === data.senderId);
                setTypingUser(user?.name || "Someone");
                setIsSeomoneTyping(true);
              }
            } else {
              if (
                data.senderId !== authUser._id &&
                data.roomId === selectedChat?.id
              ) {
                const user = joinedUsers.find((u) => u._id === data.senderId);
                setTypingUser(user?.name || "Someone");
                setIsSeomoneTyping(true);
              }
            }
          }
        }
      });

      socket.on("stop_typing", (data) => {
        if (!isEmpty(selectedChat)) {
          if (data.type === selectedChat.type) {
            if (selectedChat.type === "dm") {
              if (
                data.senderId !== authUser._id &&
                selectedChat.id === data.senderId
              ) {
                setTypingUser(null);
                setIsSeomoneTyping(false);
              }
            } else {
              if (
                data.senderId !== authUser._id &&
                data.roomId === selectedChat?.id
              ) {
                setTypingUser(null);
                setIsSeomoneTyping(false);
              }
            }
          }
        }
      });

      const updateMessage = (updated) => {
        setMessages((current) =>
          current.map((item) => (item._id === updated._id ? updated : item)),
        );
      };
      socket.on("group_message_updated", updateMessage);
      socket.on("dm_updated", updateMessage);

      return () => {
        socket.off("receive_message");
        socket.off("receive_dm");
        socket.off("receive_own");
        socket.off("typing");
        socket.off("stop_typing");
        socket.off("group_message_updated", updateMessage);
        socket.off("dm_updated", updateMessage);
      };
    }
  }, [authUser, selectedChat, joinedUsers, groupList]);

  const fetchMessages = async (pageNum = 1, appendTop = false) => {
    if (!selectedChat || !authUser) return;

    setLoading(true);

    const params =
      selectedChat.type === "dm"
        ? {
            senderId: authUser._id,
            receiverId: selectedChat.id,
            page: pageNum,
            limit: 20,
          }
        : {
            roomId: selectedChat.id,
            userId: authUser._id,
            page: pageNum,
            limit: 20,
          };

    const url =
      selectedChat.type === "dm"
        ? "/chats/get-private-chats"
        : "/chats/get-group-chats";

    try {
      const res = await apiClient.post(url, params);
      const { total, messages: newMessages } = res.data;

      const ordered = newMessages.reverse();

      if (appendTop) {
        setMessages((prev) => [...ordered, ...prev]);
      } else {
        setMessages(ordered);
      }

      setHasMore(pageNum * 20 < total);
      if (pageNum === 1)
        markConversationRead?.(selectedChat.type, selectedChat.id);
    } catch (err) {
      console.error(err);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isEmpty(selectedChat) && !isEmpty(authUser)) {
      setMessages([]);
      setPage(1);
      setHasMore(true);
      fetchMessages(1, false);
    }
  }, [selectedChat, authUser]);

  // ✅ Load more on scroll to top
  const handleScroll = async (e) => {
    const container = e.target;

    const diffScroll =
      container.scrollHeight - container.clientHeight - container.scrollTop;

    if (diffScroll > 120) {
      setScrollBottomFlag(true);
    } else {
      setScrollBottomFlag(false);
    }

    if (isFetchingRef.current || loading) return;
    if (container.scrollTop > 30 || !hasMore) return;

    isFetchingRef.current = true;
    prevScrollHeightRef.current = container.scrollHeight;

    const nextPage = page + 1;

    fetchMessages(nextPage, true);
    setPage(nextPage);
  };

  useEffect(() => {
    if (isFetchingRef.current && messagesContainerRef.current) {
      const container = messagesContainerRef.current;

      requestAnimationFrame(() => {
        const newScrollHeight = container.scrollHeight;
        const delta = newScrollHeight - prevScrollHeightRef.current;
        container.scrollTop = delta; // keep same view position
        isFetchingRef.current = false;
      });
    }
  }, [messages]);

  const scrollToDirectBottom = () => {
    if (messagesContainerRef.current) {
      requestAnimationFrame(() => {
        const container = messagesContainerRef.current;
        container.scrollTop = container.scrollHeight;
      });
    }
  };

  useEffect(() => {
    if (page === 1 && messages.length > 0) {
      scrollToDirectBottom();
    }
  }, [messages, page]);

  // Show browser notification
  const showNotification = (title, body, iconUrl) => {
    if (Notification.permission === "granted") {
      new Notification(`${title}`, {
        body,
        icon: iconUrl, // optional icon
      });
    }
  };

  const addFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    const validFiles = files.filter((file) => {
      if (file.size >= MAX_FILE_SIZE) {
        message.error(`${file.name} must be smaller than 3 MB.`);
        return false;
      }
      return true;
    });

    setPendingFiles((current) => {
      const available = Math.max(0, MAX_FILES - current.length);
      if (validFiles.length > available) {
        message.warning(`You can attach up to ${MAX_FILES} files per message.`);
      }
      return [
        ...current,
        ...validFiles.slice(0, available).map((file) => ({
          file,
          preview: file.type.startsWith("image/")
            ? URL.createObjectURL(file)
            : "",
        })),
      ];
    });
  };

  const removePendingFile = (index) => {
    setPendingFiles((current) => {
      const target = current[index];
      if (target?.preview) URL.revokeObjectURL(target.preview);
      return current.filter((_, itemIndex) => itemIndex !== index);
    });
  };

  const clearPendingFiles = () => {
    setPendingFiles((current) => {
      current.forEach(({ preview }) => preview && URL.revokeObjectURL(preview));
      return [];
    });
  };

  const handlePaste = (event) => {
    const files = Array.from(event.clipboardData?.items || [])
      .filter((item) => item.kind === "file")
      .map((item) => item.getAsFile())
      .filter(Boolean);
    if (files.length) {
      event.preventDefault();
      addFiles(files);
    }
  };

  const getLinkPreview = async (value) => {
    const url = value.match(URL_PATTERN)?.[0]?.replace(/[),.!?]+$/, "");
    if (!url) return undefined;
    try {
      const res = await apiClient.get("/chats/link-preview", {
        params: { url },
      });
      return res.data;
    } catch (_err) {
      return { url, title: new URL(url).hostname };
    }
  };

  const sendMessage = async (txt) => {
    if (
      (!txt.trim() && pendingFiles.length === 0) ||
      !selectedChat ||
      uploading
    )
      return;

    setUploading(true);
    forceBottomUntilRef.current = Date.now() + 5000;
    if (editingMessage) {
      const eventName =
        selectedChat.type === "group" ? "edit_group_message" : "edit_dm";
      socket.emit(eventName, {
        messageId: editingMessage._id,
        roomId: selectedChat.id,
        message: txt,
      });
      setEditingMessage(null);
      setText("");
      setUploading(false);
      return;
    }
    let attachments = [];
    try {
      if (pendingFiles.length) {
        const formData = new FormData();
        pendingFiles.forEach(({ file }) => formData.append("files", file));
        const uploadResponse = await apiClient.post("/chats/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        attachments = uploadResponse.data.attachments;
      }
      const linkPreview = txt.trim() ? await getLinkPreview(txt) : undefined;

      if (selectedChat.type === "group") {
        socket.emit("send_message", {
          roomId: selectedChat.id,
          message: txt,
          senderId: authUser._id,
          attachments,
          linkPreview,
          replyToId: replyingTo?._id,
          created_date: dayjs(
            dayjs(getDateAndTime(dayjs()), dateAndTimeFormat),
          ).tz(timezoneMode.timezoneMode, true),
        });

        socket.emit("stop_typing", {
          roomId: selectedChat.id,
          senderId: authUser._id,
          type: selectedChat.type,
        });
      } else {
        socket.emit("send_dm", {
          receiverId: selectedChat.id,
          message: txt,
          senderId: authUser._id,
          attachments,
          linkPreview,
          replyToId: replyingTo?._id,
          created_date: dayjs(
            dayjs(getDateAndTime(dayjs()), dateAndTimeFormat),
          ).tz(timezoneMode.timezoneMode, true),
        });

        socket.emit("stop_typing", {
          receiverId: selectedChat.id,
          senderId: authUser._id,
          type: selectedChat.type,
        });
      }

      pendingFiles.forEach(
        ({ preview }) => preview && URL.revokeObjectURL(preview),
      );
      setPendingFiles([]);
      setReplyingTo(null);
      setText("");
      scrollToDirectBottom();
    } catch (err) {
      const errorMessage =
        err?.response?.data?.errors?.[0]?.msg || "Unable to send attachment.";
      message.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (
      e.key === "ArrowUp" &&
      !text.trim() &&
      pendingFiles.length === 0 &&
      !editingMessage
    ) {
      const latestOwnMessage = [...messages]
        .reverse()
        .find((item) => item.senderId === authUser._id && item.message);
      if (latestOwnMessage) {
        e.preventDefault();
        clearPendingFiles();
        setEditingMessage(latestOwnMessage);
        setReplyingTo(null);
        setText(latestOwnMessage.message);
      }
      return;
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault(); // prevent newline
      if (text.trim() || pendingFiles.length) {
        sendMessage(text);
      }
    }
  };

  const handleChange = (e) => {
    setText(e.target.value);

    if (isEmpty(selectedChat)) return;

    socket.emit(
      "typing",
      selectedChat.type === "dm"
        ? {
            receiverId: selectedChat.id,
            senderId: authUser._id,
            type: selectedChat.type,
          }
        : {
            roomId: selectedChat.id,
            senderId: authUser._id,
            type: selectedChat.type,
          },
    );

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      socket.emit(
        "stop_typing",
        selectedChat.type === "dm"
          ? {
              receiverId: selectedChat.id,
              senderId: authUser._id,
              type: selectedChat.type,
            }
          : {
              roomId: selectedChat.id,
              senderId: authUser._id,
              type: selectedChat.type,
            },
      );
    }, 2000);
  };

  const autoResize = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto"; // reset
      textarea.style.height = `${textarea.scrollHeight}px`; // adjust height
    }
  };

  const formattedText = (value = "") => {
    return value.split(URL_PATTERN).map((part, index) =>
      /^https?:\/\//i.test(part) ? (
        <a
          key={`${part}-${index}`}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
        >
          {part}
        </a>
      ) : (
        <span key={index}>{part}</span>
      ),
    );
  };

  const openImagePreview = (url) => {
    setPreviewImage(url);
    setImageZoom(1);
    setImagePosition({ x: 0, y: 0 });
  };

  const handleImagePointerDown = (event) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    imageDragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      originX: imagePosition.x,
      originY: imagePosition.y,
    };
  };

  const handleImagePointerMove = (event) => {
    if (!imageDragRef.current) return;
    setImagePosition({
      x:
        imageDragRef.current.originX +
        event.clientX -
        imageDragRef.current.startX,
      y:
        imageDragRef.current.originY +
        event.clientY -
        imageDragRef.current.startY,
    });
  };

  const handleImagePointerUp = (event) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    imageDragRef.current = null;
  };

  const handleImageWheel = (event) => {
    event.preventDefault();
    const direction = event.deltaY < 0 ? 1 : -1;
    const nextZoom = Math.min(4, Math.max(0.25, imageZoom + direction * 0.25));
    if (nextZoom === imageZoom) return;

    const bounds = event.currentTarget.getBoundingClientRect();
    const pointerX = event.clientX - bounds.left - bounds.width / 2;
    const pointerY = event.clientY - bounds.top - bounds.height / 2;
    const ratio = nextZoom / imageZoom;

    setImagePosition((position) => ({
      x: pointerX - (pointerX - position.x) * ratio,
      y: pointerY - (pointerY - position.y) * ratio,
    }));
    setImageZoom(nextZoom);
  };

  useEffect(() => {
    autoResize();
  }, [text]);

  useEffect(() => {
    setPendingFiles((current) => {
      current.forEach(({ preview }) => preview && URL.revokeObjectURL(preview));
      return [];
    });
  }, [selectedChat]);

  useEffect(() => {
    const closeMenu = () => setActionMenu(null);
    window.addEventListener("click", closeMenu);
    window.addEventListener("resize", closeMenu);
    return () => {
      window.removeEventListener("click", closeMenu);
      window.removeEventListener("resize", closeMenu);
    };
  }, []);

  return (
    <div
      className={`chat-box-section ${
        themeMode.themeMode ? "chat-box-dark" : "chat-box-light"
      }`}
    >
      {isEmpty(selectedChat) ? (
        <div className="chat-box-empty">
          <div className="chat-empty-icon">
            <TeamOutlined />
          </div>
          <h2>Your conversations</h2>
          <p>Select a channel or teammate to start collaborating.</p>
        </div>
      ) : (
        <div className="chat-box-wrapper">
          <header className="conversation-header">
            <div className="conversation-header-icon">
              {selectedChat.type === "group" ? (
                <NumberOutlined />
              ) : (
                <UserOutlined />
              )}
            </div>
            <div className="conversation-header-copy">
              <h2>{selectedChat.name}</h2>
              <p>
                {selectedChat.type === "group"
                  ? "Team channel"
                  : onlineUsers?.includes(selectedChat.id)
                    ? "Online now"
                    : "Offline"}
              </p>
            </div>
          </header>
          {loading && page === 1 && (
            <div className="chat-view-loading" role="status" aria-live="polite">
              <div className="chat-view-loading-indicator">
                <Spin size="large" />
                <span>Loading messages...</span>
              </div>
            </div>
          )}
          <div
            className="messages"
            ref={messagesContainerRef}
            onScroll={handleScroll}
          >
            {loading && page > 1 && (
              <div className="older-messages-loading">
                <Spin size="small" />
              </div>
            )}

            {isEmpty(messages) ? (
              <div className="messages-empty">
                <div className="messages-empty-icon">
                  {selectedChat.type === "group" ? (
                    <NumberOutlined />
                  ) : (
                    <UserOutlined />
                  )}
                </div>
                <h3>Start the conversation</h3>
                <p>Send the first message to {selectedChat.name}.</p>
              </div>
            ) : (
              <div className="messages-content">
                {messages.map((msg) => {
                  const user = joinedUsers.find((u) => u._id === msg.senderId);
                  const isMine = authUser._id === msg.senderId;

                  return (
                    <div
                      className={`message-row ${isMine ? "message-row-me" : ""}`}
                      key={msg._id || `${msg.senderId}-${msg.created_date}`}
                      onContextMenu={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        const wrapper =
                          event.currentTarget.closest(".chat-box-wrapper");
                        const wrapperBounds = wrapper.getBoundingClientRect();
                        const messageBounds =
                          messagesContainerRef.current.getBoundingClientRect();
                        const menuWidth = 128;
                        const menuHeight = isMine && msg.message ? 84 : 46;
                        const edgeGap = 8;
                        const minY =
                          messageBounds.top - wrapperBounds.top + edgeGap;
                        const maxY =
                          messageBounds.bottom -
                          wrapperBounds.top -
                          menuHeight -
                          edgeGap;
                        let x = event.clientX - wrapperBounds.left;
                        let y = event.clientY - wrapperBounds.top;

                        if (x + menuWidth > wrapperBounds.width - edgeGap) {
                          x = wrapperBounds.width - menuWidth - edgeGap;
                        }
                        if (y > maxY) y -= menuHeight;

                        setActionMenu({
                          x: Math.max(edgeGap, x),
                          y: Math.max(minY, Math.min(y, maxY)),
                          message: msg,
                          isMine,
                        });
                      }}
                    >
                      {!isMine && (
                        <div className="sender-avatar">
                          {user?.avatar ? (
                            <img
                              src={user.avatar}
                              alt=""
                              className="sender-img"
                            />
                          ) : (
                            <UserOutlined />
                          )}
                        </div>
                      )}
                      <div className="message-body">
                        {msg.replyTo?.messageId && (
                          <div className="message-reply-quote">
                            <strong>Reply</strong>
                            <span>
                              {msg.replyTo.message ||
                                msg.replyTo.attachmentName ||
                                "Attachment"}
                            </span>
                          </div>
                        )}
                        {!isMine && (
                          <div className="message-name">
                            {user?.name || "Team member"}
                          </div>
                        )}
                        {msg.message && (
                          <div className="message-text">
                            {formattedText(msg.message)}
                          </div>
                        )}
                        {!!msg.attachments?.length && (
                          <div className="message-attachments">
                            {msg.attachments.map(
                              (attachment, attachmentIndex) => {
                                const fileUrl = `${API_BASE_URL}${attachment.url}`;
                                if (attachment.mimeType?.startsWith("image/")) {
                                  return (
                                    <button
                                      type="button"
                                      className="chat-image-attachment"
                                      key={`${attachment.url}-${attachmentIndex}`}
                                      onClick={() => openImagePreview(fileUrl)}
                                    >
                                      <img
                                        src={fileUrl}
                                        alt={attachment.name}
                                        loading="lazy"
                                        onLoad={() => {
                                          if (
                                            Date.now() <
                                              forceBottomUntilRef.current &&
                                            !isFetchingRef.current
                                          ) {
                                            scrollToDirectBottom();
                                          }
                                        }}
                                      />
                                    </button>
                                  );
                                }
                                if (attachment.mimeType?.startsWith("video/")) {
                                  return (
                                    <video
                                      key={`${attachment.url}-${attachmentIndex}`}
                                      className="chat-video-attachment"
                                      controls
                                      preload="metadata"
                                      onLoadedMetadata={() => {
                                        if (
                                          Date.now() <
                                            forceBottomUntilRef.current &&
                                          !isFetchingRef.current
                                        )
                                          scrollToDirectBottom();
                                      }}
                                    >
                                      <source
                                        src={fileUrl}
                                        type={attachment.mimeType}
                                      />
                                    </video>
                                  );
                                }
                                return (
                                  <a
                                    className="chat-file-attachment"
                                    key={`${attachment.url}-${attachmentIndex}`}
                                    href={fileUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    download={attachment.name}
                                  >
                                    <FileOutlined />
                                    <span>
                                      <strong>{attachment.name}</strong>
                                      <small>
                                        {(attachment.size / 1024).toFixed(1)} KB
                                      </small>
                                    </span>
                                  </a>
                                );
                              },
                            )}
                          </div>
                        )}
                        {msg.linkPreview?.url && (
                          <a
                            className="chat-link-preview"
                            href={msg.linkPreview.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {msg.linkPreview.image && (
                              <img
                                src={msg.linkPreview.image}
                                alt=""
                                loading="lazy"
                                onLoad={() => {
                                  if (
                                    Date.now() < forceBottomUntilRef.current &&
                                    !isFetchingRef.current
                                  )
                                    scrollToDirectBottom();
                                }}
                              />
                            )}
                            <span>
                              <small>
                                {msg.linkPreview.siteName ||
                                  new URL(msg.linkPreview.url).hostname}
                              </small>
                              <strong>
                                {msg.linkPreview.title || msg.linkPreview.url}
                              </strong>
                              {msg.linkPreview.description && (
                                <em>{msg.linkPreview.description}</em>
                              )}
                            </span>
                          </a>
                        )}
                        <div className="message-time">
                          {getTime(msg.created_date)}
                          {msg.edited_at && <span> · edited</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          {actionMenu && (
            <div
              className="chat-message-menu"
              style={{ left: actionMenu.x, top: actionMenu.y }}
              onClick={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => {
                  setReplyingTo(actionMenu.message);
                  setEditingMessage(null);
                  setActionMenu(null);
                  textareaRef.current?.focus();
                }}
              >
                Reply
              </button>
              {actionMenu.isMine && actionMenu.message.message && (
                <button
                  type="button"
                  onClick={() => {
                    clearPendingFiles();
                    setEditingMessage(actionMenu.message);
                    setReplyingTo(null);
                    setText(actionMenu.message.message);
                    setActionMenu(null);
                    textareaRef.current?.focus();
                  }}
                >
                  Edit
                </button>
              )}
            </div>
          )}
          <div className="input-section">
            {(editingMessage || replyingTo) && (
              <div className="composer-context">
                <div>
                  <strong>
                    {editingMessage ? "Editing message" : "Replying to message"}
                  </strong>
                  <span>
                    {(editingMessage || replyingTo).message ||
                      (editingMessage || replyingTo).attachments?.[0]?.name ||
                      "Attachment"}
                  </span>
                </div>
                <Button
                  type="text"
                  icon={<CloseOutlined />}
                  onClick={() => {
                    if (editingMessage) setText("");
                    setEditingMessage(null);
                    setReplyingTo(null);
                  }}
                />
              </div>
            )}
            {!!pendingFiles.length && (
              <div className="pending-attachments">
                {pendingFiles.map(({ file, preview }, index) => (
                  <div
                    className="pending-attachment"
                    key={`${file.name}-${file.lastModified}-${index}`}
                  >
                    {preview ? (
                      <img src={preview} alt={file.name} />
                    ) : (
                      <FileOutlined />
                    )}
                    <span title={file.name}>{file.name}</span>
                    <Button
                      type="text"
                      size="small"
                      icon={<CloseOutlined />}
                      onClick={() => removePendingFile(index)}
                      aria-label={`Remove ${file.name}`}
                    />
                  </div>
                ))}
              </div>
            )}
            {scrollBottomFlag && (
              <div className="scroll-bottom-flag-btn">
                <Button
                  type="primary"
                  size="large"
                  shape="circle"
                  icon={<ArrowDownOutlined />}
                  onClick={() => scrollToDirectBottom()}
                />
              </div>
            )}

            <input
              ref={fileInputRef}
              className="chat-file-input"
              type="file"
              multiple
              onChange={(event) => {
                addFiles(event.target.files);
                event.target.value = "";
              }}
            />
            <Button
              type="text"
              shape="circle"
              size="large"
              className="attach-file-button"
              icon={<PaperClipOutlined />}
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              aria-label="Attach files"
            />
            <textarea
              ref={textareaRef}
              value={text}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              placeholder={`Message ${selectedChat?.name || ""}`}
              rows={1}
              className="textarea-input"
            />
            <Button
              type="primary"
              shape="circle"
              size="large"
              className="send-message-button"
              icon={<SendOutlined />}
              loading={uploading}
              disabled={
                (!text.trim() && pendingFiles.length === 0) || uploading
              }
              onClick={() => sendMessage(text)}
              aria-label="Send message"
            />
            <div
              className={`composer-hint ${isSomeoneTyping ? "composer-hint-typing" : ""}`}
            >
              {isSomeoneTyping ? (
                <>
                  <span className="typing-dots">
                    <i></i>
                    <i></i>
                    <i></i>
                  </span>
                  {typingUser} is typing
                </>
              ) : (
                <>Enter to send · Shift + Enter for a new line</>
              )}
            </div>
          </div>
        </div>
      )}
      <Modal
        rootClassName={`chat-image-modal ${themeMode.themeMode ? "chat-image-modal--dark" : ""}`}
        open={!!previewImage}
        onCancel={() => {
          setPreviewImage("");
          setImagePosition({ x: 0, y: 0 });
        }}
        footer={null}
        width="min(92vw, 1100px)"
        centered
        destroyOnHidden
      >
        <div className="chat-image-toolbar">
          <Button
            type="text"
            className="chat-image-zoom-button"
            aria-label="Zoom out"
            icon={<ZoomOutOutlined />}
            disabled={imageZoom <= 0.25}
            onClick={() => setImageZoom((zoom) => Math.max(0.25, zoom - 0.25))}
          />
          <span>{Math.round(imageZoom * 100)}%</span>
          <Button
            type="text"
            className="chat-image-zoom-button"
            aria-label="Zoom in"
            icon={<ZoomInOutlined />}
            disabled={imageZoom >= 4}
            onClick={() => setImageZoom((zoom) => Math.min(4, zoom + 0.25))}
          />
        </div>
        <div
          className={`chat-image-stage ${imageDragRef.current ? "is-dragging" : ""}`}
          onPointerDown={handleImagePointerDown}
          onPointerMove={handleImagePointerMove}
          onPointerUp={handleImagePointerUp}
          onPointerCancel={handleImagePointerUp}
          onWheel={handleImageWheel}
        >
          <img
            src={previewImage}
            alt="Chat attachment preview"
            draggable="false"
            style={{
              transform: `translate3d(${imagePosition.x}px, ${imagePosition.y}px, 0) scale(${imageZoom})`,
            }}
          />
        </div>
      </Modal>
    </div>
  );
};

export default Chatbox;
